import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Close, ExpandMore, PhotoCamera, Refresh, Videocam } from "@mui/icons-material";
import { cameraAvailability, cameraMetric, formatCameraDate } from "../model/cameraModel";

const display = (value) =>
  value === null || value === undefined || value === "" || value === "No disponible"
    ? "Sin información"
    : String(value);
function Field({ label, value }) {
  return (
    <Box>
      <Typography sx={{ color: "#657c90", fontSize: 12, mb: 0.5 }}>{label}</Typography>
      <Typography
        sx={{ color: "#253f53", fontSize: 14, fontWeight: 600, overflowWrap: "anywhere" }}
      >
        {display(value)}
      </Typography>
    </Box>
  );
}
Field.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.any };

function CameraDetailModal({
  open,
  onClose,
  detalle,
  loading,
  errorConsulta,
  imagenCamara,
  cargandoImagen,
  errorImagen,
  onReintentarImagen,
  onConsultar,
}) {
  const [imageError, setImageError] = useState(false);
  useEffect(() => setImageError(false), [detalle?.ip, imagenCamara?.url]);
  if (!detalle) return null;
  const availability = cameraAvailability(detalle);
  const cpu = cameraMetric(detalle.cpuUtilization);
  const uploads = Array.isArray(detalle.uploads) ? detalle.uploads : [];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          m: { xs: 1, sm: 3 },
          width: { xs: "calc(100% - 16px)", sm: "calc(100% - 48px)" },
          maxHeight: "90dvh",
          bgcolor: "#f5f8fb",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#193d50",
          color: "#fff",
          p: 2.5,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Videocam />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h2"
            sx={{ fontSize: 19, fontWeight: 700, color: "#fff", overflowWrap: "anywhere" }}
          >
            {detalle.name || "Detalle de cámara"}
          </Typography>
          <Typography sx={{ color: "#d4e4ed", fontSize: 13 }}>
            {detalle.commerceName || ""} · {detalle.ip}
          </Typography>
        </Box>
        <IconButton aria-label="Cerrar detalle" onClick={onClose} sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 2 }}>
          <Chip
            size="small"
            variant="outlined"
            label={availability.label}
            color={availability.color}
          />
          <Chip
            size="small"
            label={
              detalle.status === 0
                ? "Monitoreo habilitado"
                : detalle.status === 1
                ? "Monitoreo deshabilitado"
                : "Monitoreo sin información"
            }
          />
          {detalle.maintenance_status === 1 && (
            <Chip
              size="small"
              label="En mantenimiento"
              sx={{ color: "#4657a1", bgcolor: "#eef0fb" }}
            />
          )}
        </Box>
        {detalle.error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {detalle.error}
          </Alert>
        )}
        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography sx={{ fontSize: 13, mt: 1 }}>Consultando el estado del equipo…</Typography>
          </Box>
        )}
        {errorConsulta && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" onClick={() => onConsultar(detalle.ip)} disabled={loading}>
                Reintentar
              </Button>
            }
          >
            {errorConsulta}
          </Alert>
        )}
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                bgcolor: "#fff",
                border: "1px solid #dce5ed",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: 700, color: "#25465c", fontSize: 15 }}>
                  Imagen de la cámara
                </Typography>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  disabled={cargandoImagen}
                  onClick={() => onReintentarImagen(detalle.ip)}
                  sx={{ "&&": { color: "#245e89" } }}
                >
                  {imagenCamara ? "Actualizar" : "Obtener imagen"}
                </Button>
              </Box>
              <Box
                sx={{
                  minHeight: 280,
                  bgcolor: "#eaf0f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1,
                }}
              >
                {cargandoImagen ? (
                  <CircularProgress />
                ) : imagenCamara?.url && !imageError ? (
                  <Box
                    component="img"
                    src={imagenCamara.url}
                    alt={`Captura de ${detalle.name || detalle.ip}`}
                    onError={() => setImageError(true)}
                    sx={{
                      display: "block",
                      width: "100%",
                      maxHeight: 440,
                      objectFit: "contain",
                      borderRadius: 1,
                    }}
                  />
                ) : (
                  <Box sx={{ textAlign: "center", p: 3, color: "#5b7286" }}>
                    <PhotoCamera sx={{ fontSize: 42 }} />
                    <Typography sx={{ fontSize: 14, mt: 1 }}>
                      {errorImagen ||
                        (imageError
                          ? "La imagen recibida no pudo visualizarse."
                          : "Solicita una captura para ver la cámara.")}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography sx={{ p: 2, fontSize: 12, color: "#61788c" }}>
                {imagenCamara?.timestamp
                  ? `Captura recibida: ${formatCameraDate(imagenCamara.timestamp)} · Colombia`
                  : "Imagen bajo demanda; no es una transmisión en vivo."}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 2, border: "1px solid #dce5ed" }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2, color: "#25465c" }}>
                Última consulta del equipo
              </Typography>
              <Box sx={{ p: 2, bgcolor: "#eef4fb", borderRadius: 2, mb: 2 }}>
                <Field label="Uso de CPU" value={cpu === null || cpu > 100 ? null : `${cpu}%`} />
                {cpu !== null && cpu <= 100 && (
                  <Box
                    role="progressbar"
                    aria-label="Uso de CPU"
                    aria-valuenow={cpu}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    sx={{
                      mt: 1.5,
                      height: 7,
                      borderRadius: 4,
                      bgcolor: "#d9e5ef",
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ height: "100%", width: `${cpu}%`, bgcolor: "#397da4" }} />
                  </Box>
                )}
              </Box>
              <Box sx={{ p: 2, bgcolor: "#eef6f3", borderRadius: 2, mb: 2 }}>
                <Field label="Memoria usada · valor reportado" value={detalle.memoryUsage} />
                <Typography sx={{ color: "#61788c", fontSize: 12, mt: 1 }}>
                  Disponible · valor reportado: {display(detalle.memoryAvailable)}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    label="Tiempo encendida · reportado por el equipo"
                    value={detalle.uptime}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field label="Versión" value={detalle.version} />
                </Grid>
                <Grid item xs={6}>
                  <Field label="Host" value={detalle.hostid} />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    label="Consulta recibida · Colombia"
                    value={detalle.queriedAt ? formatCameraDate(detalle.queriedAt) : null}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: "1px solid #dce5ed",
                borderRadius: "8px !important",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#294b62" }}>
                  Detalles técnicos
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {[
                    ["Inicio del equipo · valor reportado", detalle.bootTime],
                    ["Tiempo de cámara", detalle.cameraRunTime],
                    ["Última calibración · valor reportado", detalle.lastCalibrationTime],
                    ["Memoria usada · valor original", detalle.memoryUsage],
                    ["Memoria disponible · valor original", detalle.memoryAvailable],
                    ["Caché · valor original", detalle.cacheSize],
                    ["DNS", detalle.dns],
                    ["Puerto", detalle.port],
                  ].map(([label, value]) => (
                    <Grid key={label} item xs={12} sm={6} md={4}>
                      <Field label={label} value={value} />
                    </Grid>
                  ))}
                </Grid>
                {uploads.length > 0 && (
                  <Box sx={{ overflowX: "auto", mt: 3 }}>
                    <Typography sx={{ fontSize: 14, mb: 1, fontWeight: 600 }}>
                      Subidas reportadas
                    </Typography>
                    <Table size="small">
                      <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow>
                          {[
                            "Destino",
                            "Tipo",
                            "Estado",
                            "Completadas",
                            "Total",
                            "Tiempo promedio",
                            "Tiempo máximo",
                          ].map((v) => (
                            <TableCell key={v}>{v}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploads.map((upload, index) => (
                          <TableRow key={index}>
                            {[
                              upload.uploadIpaddr,
                              upload.uploadType,
                              upload.uploadStatus,
                              upload.uploadSuccessNum,
                              upload.uploadTotalNum,
                              upload.avgTime,
                              upload.maxTime,
                            ].map((v, i) => (
                              <TableCell key={i}>{display(v)}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ bgcolor: "#fff", px: 3, py: 2, borderTop: "1px solid #dce5ed" }}>
        <Button
          startIcon={<Refresh />}
          disabled={loading}
          onClick={() => onConsultar(detalle.ip)}
          sx={{ "&&": { color: "#245e89" } }}
        >
          Consultar estado
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ "&&": { bgcolor: "#193d50", color: "#fff" } }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
CameraDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  detalle: PropTypes.object,
  loading: PropTypes.bool,
  errorConsulta: PropTypes.string,
  imagenCamara: PropTypes.object,
  cargandoImagen: PropTypes.bool,
  errorImagen: PropTypes.string,
  onReintentarImagen: PropTypes.func.isRequired,
  onConsultar: PropTypes.func.isRequired,
};
export default CameraDetailModal;
